/**
 * Chat history — every conversation a child has had, named automatically after
 * the first couple of exchanges, searchable, renameable and deletable.
 * Chats are kept for 30 days.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPatch } from '@/lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Check, Loader2, MessageSquare, Pencil, Search, Trash2, X } from 'lucide-react';

const when = (s) => {
  if (!s) return '';
  const d = new Date(s);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export function ChatHistoryDrawer({ open, onOpenChange, activeSessionId, onOpenSession }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');

  const load = useCallback(async (term) => {
    setLoading(true);
    try {
      const d = await apiGet(`/chat/sessions${term ? `?q=${encodeURIComponent(term)}` : ''}`);
      setRows(d.sessions || []);
    } catch { /* a quiet list is better than a red toast here */ }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(''); }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => load(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q, open, load]);

  const rename = async (id) => {
    const title = draft.trim();
    if (!title) { setEditing(null); return; }
    try {
      await apiPatch(`/chat/sessions/${id}`, { title });
      setRows(prev => prev.map(r => (r.session_id === id ? { ...r, title, label: title } : r)));
      toast.success('Renamed');
    } catch { toast.error('Could not rename that chat'); }
    setEditing(null);
  };

  const remove = async (id) => {
    try {
      await apiDelete(`/chat/sessions/${id}`);
      setRows(prev => prev.filter(r => r.session_id !== id));
      toast.success('Chat deleted');
    } catch { toast.error('Could not delete that chat'); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[340px] sm:w-[400px] p-0 flex flex-col" data-testid="chat-history-drawer">
        <SheetHeader className="px-5 pt-5 pb-3">
          <SheetTitle className="font-heading text-lg text-slate-900">Your chats</SheetTitle>
          <p className="text-[11px] font-body text-slate-500">
            Named automatically, searchable, and kept for 30 days.
          </p>
        </SheetHeader>

        <div className="px-5 pb-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus-within:border-blue-300">
            <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <input data-testid="chat-history-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search what you talked about…"
              className="flex-1 bg-transparent text-xs font-body text-slate-700 placeholder-slate-400 outline-none" />
            {loading && <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-5 space-y-1">
          {!loading && rows.length === 0 && (
            <p className="text-xs font-body text-slate-400 px-2 py-6 text-center" data-testid="chat-history-empty">
              {q ? 'Nothing matches that yet.' : 'No chats yet — ask your buddy anything to begin.'}
            </p>
          )}
          {rows.map(s => (
            <div key={s.session_id}
              data-testid={`history-row-${s.session_id}`}
              className={`group rounded-xl px-3 py-2.5 transition-colors ${activeSessionId === s.session_id ? 'bg-blue-50 ring-1 ring-blue-100' : 'hover:bg-slate-100'}`}>
              {editing === s.session_id ? (
                <div className="flex items-center gap-1.5">
                  <input autoFocus data-testid="history-rename-input" value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') rename(s.session_id); if (e.key === 'Escape') setEditing(null); }}
                    className="flex-1 min-w-0 bg-white border border-blue-200 rounded-lg px-2 py-1 text-xs font-body outline-none" />
                  <button data-testid="history-rename-save" onClick={() => rename(s.session_id)}
                    className="w-6 h-6 rounded-md bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3" />
                  </button>
                  <button data-testid="history-rename-cancel" onClick={() => setEditing(null)}
                    className="w-6 h-6 rounded-md bg-slate-200 text-slate-600 flex items-center justify-center flex-shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <button className="flex-1 min-w-0 text-left" data-testid={`history-open-${s.session_id}`}
                    onClick={() => { onOpenSession?.(s.session_id); onOpenChange?.(false); }}>
                    <p className="font-heading text-[13px] text-slate-800 truncate flex items-center gap-1.5">
                      <MessageSquare className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      {s.label || s.topic || s.subject || 'Ask me anything'}
                    </p>
                    <p className="text-[10px] font-body text-slate-400 mt-0.5 truncate pl-4">
                      {[s.subject, s.topic].filter(Boolean).join(' · ') || 'Open chat'} · {when(s.last_message_at)}
                    </p>
                  </button>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <button data-testid={`history-rename-${s.session_id}`} title="Rename"
                      onClick={() => { setEditing(s.session_id); setDraft(s.label || s.topic || ''); }}
                      className="w-6 h-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-white flex items-center justify-center">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button data-testid={`history-delete-${s.session_id}`} title="Delete"
                      onClick={() => remove(s.session_id)}
                      className="w-6 h-6 rounded-md text-slate-400 hover:text-rose-600 hover:bg-white flex items-center justify-center">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ChatHistoryDrawer;
